import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { wallTimeInZone } from '@kraftwerk/shared';
import type { NoteEventKind } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import type { LocalJournalNote, LocalJournalPhoto } from '../../db/local.js';
import { listAgenda } from '../../db/calendar.js';
import type { AgendaItem } from '../../db/calendar.js';
import { addNotePhoto, deleteNote, notesOfDay, photosOfNote, saveNote } from '../../db/notes.js';
import { resizeToJpeg } from '../../db/photos.js';
import { formatDay } from '../../format.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { ScalePicker } from '../ScalePicker.js';
import { PhotoImage } from './JournalPhotos.js';

export interface NoteEventPreset {
  kind: NoteEventKind;
  id: string;
  title: string;
  date: string;
}

/**
 * Gedanken eines Tages (E14) — unter dem Check-in, gemischt in derselben
 * Tagesansicht. Frei geschrieben, optional mit Stimmung, Fotos und Termin.
 */
export function DayNotes({
  date,
  preset = null,
  onPresetUsed,
  startNew = false,
}: {
  date: string;
  /** Aus der Heute-Karte „Gedanke“: gleich ein neuer. */
  startNew?: boolean;
  /** Aus dem Kalender: „Eintrag dazu schreiben" öffnet den Editor mit diesem Termin. */
  preset?: NoteEventPreset | null;
  onPresetUsed?: (() => void) | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const notes = useLiveQuery(() => notesOfDay(date, db), [date], [] as LocalJournalNote[]);
  const [editing, setEditing] = useState<LocalJournalNote | 'new' | null>(startNew ? 'new' : null);
  const [eventPreset, setEventPreset] = useState<NoteEventPreset | null>(null);

  useEffect(() => {
    if (!preset) return;
    setEventPreset(preset);
    setEditing('new');
    onPresetUsed?.();
  }, [preset, onPresetUsed]);

  return (
    <section
      id="journal-day-notes"
      aria-labelledby={`notes-${date}`}
      className="flex scroll-mt-24 flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 id={`notes-${date}`} className="kw-kicker">
          {t('notes.heading')}
        </h3>
        {editing ? null : (
          <button
            type="button"
            onClick={() => {
              setEventPreset(null);
              setEditing('new');
            }}
            className="kw-btn kw-btn-small px-3"
          >
            {t('notes.add')}
          </button>
        )}
      </div>

      {editing ? (
        <NoteEditor
          date={date}
          note={editing === 'new' ? null : editing}
          preset={editing === 'new' ? eventPreset : null}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {notes.length === 0 && !editing ? (
        <p className="text-sm text-ink-muted">{t('notes.empty')}</p>
      ) : null}
      <ul className="flex flex-col gap-3">
        {notes
          .filter((note) => editing === 'new' || editing?.id !== note.id)
          .map((note) => (
            <li key={note.id}>
              <NoteCard note={note} onEdit={() => setEditing(note)} />
            </li>
          ))}
      </ul>
    </section>
  );
}

export function NoteCard({
  note,
  onEdit,
}: {
  note: LocalJournalNote;
  onEdit?: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const photos = useLiveQuery(
    () => photosOfNote(note.id, db),
    [note.id],
    [] as LocalJournalPhoto[],
  );
  return (
    <article className="kw-tile flex flex-col gap-2">
      <header className="flex items-baseline justify-between gap-2">
        <span className="min-w-0">
          <span className="text-xs text-ink-muted">{note.time}</span>
          {note.title ? <span className="ml-2 font-semibold break-words">{note.title}</span> : null}
        </span>
        {note.mood !== null ? (
          <span className="shrink-0 text-xs text-ink-muted">
            {t('notes.moodValue', { value: note.mood })}
          </span>
        ) : null}
      </header>
      {note.eventTitle ? (
        <p className="text-xs text-ink-muted">
          {t('notes.about', {
            title: note.eventTitle,
            date: note.eventDate ? formatDay(note.eventDate, 'short', i18n.language) : '',
          })}
        </p>
      ) : null}
      {note.body ? <p className="text-sm whitespace-pre-wrap break-words">{note.body}</p> : null}
      {photos.length > 0 ? (
        <ul className="grid grid-cols-3 gap-2">
          {photos.map((photo) => (
            <li key={photo.id}>
              <PhotoImage photo={photo} />
            </li>
          ))}
        </ul>
      ) : null}
      {note.tags.length > 0 ? (
        <ul className="flex flex-wrap gap-1">
          {note.tags.map((tag) => (
            <li key={tag} className="kw-chip">
              #{tag}
            </li>
          ))}
        </ul>
      ) : null}
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
        >
          {t('notes.edit')}
        </button>
      ) : null}
    </article>
  );
}

function NoteEditor({
  date,
  note,
  preset,
  onClose,
}: {
  date: string;
  note: LocalJournalNote | null;
  preset: NoteEventPreset | null;
  onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone() ?? 'Europe/Berlin';
  const ids = { title: useId(), body: useId(), time: useId(), event: useId(), photo: useId() };
  const [saved, setSaved] = useState<LocalJournalNote | null>(note);
  const [title, setTitle] = useState(note?.title ?? '');
  const [body, setBody] = useState(note?.body ?? '');
  const [mood, setMood] = useState<number | null>(note?.mood ?? null);
  const [time, setTime] = useState(note?.time ?? wallTimeInZone(new Date(), timeZone));
  const initialEvent: NoteEventPreset | null =
    preset ??
    (note?.eventKind && note.eventId
      ? {
          kind: note.eventKind,
          id: note.eventId,
          title: note.eventTitle ?? '',
          date: note.eventDate ?? date,
        }
      : null);
  const [event, setEvent] = useState<NoteEventPreset | null>(initialEvent);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const me = currentOwnerId();
  const trainingTitle = t('calendar.training');
  const agenda = useLiveQuery(
    () => listAgenda(date, date, { timeZone, me, trainingTitle }, db),
    [date, timeZone, me, trainingTitle],
    [] as AgendaItem[],
  );
  const choices = agenda.filter((item) => item.kind !== 'task');
  const photos = useLiveQuery(
    () => (saved ? photosOfNote(saved.id, db) : Promise.resolve([] as LocalJournalPhoto[])),
    [saved?.id],
    [] as LocalJournalPhoto[],
  );

  const persist = async () =>
    saveNote(date, { title, body, mood, time, event }, saved?.id ?? null, db).then((row) => {
      setSaved(row);
      return row;
    });

  const attach = async (file: File) => {
    setBusy(true);
    try {
      const target = saved ?? (await persist());
      await addNotePhoto(target, await resizeToJpeg(file), db);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      aria-label={note ? t('notes.edit') : t('notes.add')}
      className="kw-tile flex flex-col gap-3 border-accent"
      onSubmit={(submit) => {
        submit.preventDefault();
        if (body.trim() === '' && title.trim() === '' && photos.length === 0) return;
        void persist().then(onClose);
      }}
    >
      <div className="flex gap-2">
        <label htmlFor={ids.title} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="kw-label">{t('notes.title')}</span>
          <input
            id={ids.title}
            value={title}
            maxLength={200}
            onChange={(change) => setTitle(change.target.value)}
            className="kw-field"
          />
        </label>
        <label htmlFor={ids.time} className="flex w-28 flex-col gap-1">
          <span className="kw-label">{t('notes.time')}</span>
          <input
            id={ids.time}
            type="time"
            value={time}
            onChange={(change) => setTime(change.target.value)}
            className="kw-field"
          />
        </label>
      </div>
      <label htmlFor={ids.body} className="flex flex-col gap-1">
        <span className="kw-label">{t('notes.body')}</span>
        {/* Ein neuer Gedanke springt ins Schreibfeld: „Gedanke“ auf der
            Heute-Karte versprach das, setzte den Fokus aber nie — die
            Tastatur blieb zu (Geräteabnahme 28.10.2026). Beim Bearbeiten
            nicht: da will man erst lesen. */}
        <textarea
          id={ids.body}
          value={body}
          autoFocus={note === null}
          rows={6}
          maxLength={20_000}
          onChange={(change) => setBody(change.target.value)}
          placeholder={t('notes.placeholder')}
          className="kw-field"
        />
        <span className="text-xs text-ink-muted">{t('notes.tagsHint')}</span>
      </label>
      <ScalePicker
        label={t('notes.mood')}
        low={t('journal.scale.bad')}
        high={t('journal.scale.good')}
        value={mood}
        onChange={setMood}
      />
      <label htmlFor={ids.event} className="flex flex-col gap-1">
        <span className="kw-label">{t('notes.event')}</span>
        <select
          id={ids.event}
          value={event ? `${event.kind}|${event.id}` : ''}
          onChange={(change) => {
            const item = choices.find(
              (entry) => `${entry.kind}|${entry.id}` === change.target.value,
            );
            setEvent(
              item
                ? {
                    kind: item.kind as NoteEventKind,
                    id: item.id,
                    title: item.title,
                    date: item.date,
                  }
                : null,
            );
          }}
          className="kw-field"
        >
          <option value="">{t('notes.noEvent')}</option>
          {event && !choices.some((item) => item.id === event.id) ? (
            <option value={`${event.kind}|${event.id}`}>
              {event.title} · {formatDay(event.date, 'short', i18n.language)}
            </option>
          ) : null}
          {choices.map((item) => (
            <option key={item.key} value={`${item.kind}|${item.id}`}>
              {item.time ? `${item.time} · ` : ''}
              {item.title}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-col gap-2">
        <span className="kw-label">{t('journal.photos.heading')}</span>
        {photos.length > 0 ? (
          <ul className="grid grid-cols-3 gap-2">
            {photos.map((photo) => (
              <li key={photo.id}>
                <PhotoImage photo={photo} />
              </li>
            ))}
          </ul>
        ) : null}
        <label
          htmlFor={ids.photo}
          className={`kw-btn kw-btn-quiet kw-btn-small cursor-pointer ${busy ? 'opacity-50' : ''}`}
        >
          {busy ? t('journal.photos.preparing') : t('journal.photos.add')}
        </label>
        <input
          id={ids.photo}
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={busy}
          onChange={(change) => {
            const file = change.target.files?.[0];
            change.target.value = '';
            if (!file) return;
            void attach(file);
          }}
        />
      </div>

      <div className="flex gap-2">
        <button type="submit" className="kw-btn flex-1">
          {t('notes.save')}
        </button>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet flex-1">
          {t('nutrition.library.cancel')}
        </button>
      </div>
      {saved ? (
        confirmDelete ? (
          <div className="flex flex-col gap-2 rounded-xl border border-danger p-3">
            <p className="text-sm">{t('notes.deleteWarning')}</p>
            <button
              type="button"
              onClick={() => void deleteNote(saved.id, db).then(onClose)}
              className="kw-btn kw-btn-small border-danger text-danger"
            >
              {t('notes.delete')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('notes.delete')}
          </button>
        )
      ) : null}
    </form>
  );
}
